import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams, useHistory } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Progress,
  Badge,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import cogoToast from "cogo-toast";
import { genericGet, genericPost } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT, DEFAULT_RESULT } from "../../Constants";

const POLL_INTERVAL_MS = 3000;

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const formatMasterlistType = (value) => {
  const v = String(value ?? "");
  if (v === "10000") return "Registration MasterList";
  if (v === "10001") return "Attendance MasterList";
  return value || "—";
};

const formatLocation = (location) => {
  if (!location) return "—";
  const parts = String(location)
    .split("-")
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" → ") : location;
};

const getStatusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "failed") return "danger";
  if (s === "cancelled") return "warning";
  if (s === "generating" || s === "processing" || s === "running") return "info";
  if (s === "preparing" || s === "queued") return "warning";
  return "secondary";
};

const isTerminalStatus = (status) => {
  const s = String(status || "").toLowerCase();
  return s === "completed" || s === "failed" || s === "cancelled";
};

const isCancellableStatus = (status) => {
  const s = String(status || "").toLowerCase();
  return s === "queued" || s === "preparing" || s === "generating" || s === "processing" || s === "running";
};

const DetailRow = ({ label, value }) => (
  <div className="mb-2">
    <div className="text-muted small">{label}</div>
    <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{value ?? "—"}</div>
  </div>
);

const OtherActiveJobCard = ({ job, onTrack }) => {
  const jobStatus = String(job?.status || "unknown").toLowerCase();
  const jobProgress = Math.min(100, Math.max(0, Number(job?.progress) || 0));
  const jobMeta = job?.meta || {};
  const jobTotalCamps = Number(job?.total_camps) || 0;
  const jobCompletedCamps = Number(job?.completed_camps) || 0;

  return (
    <Card className="shadow-sm mb-3" style={{ border: "1px solid #e2e8f0" }}>
      <CardBody className="p-3">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div style={{ fontWeight: 700, fontSize: "0.9rem", wordBreak: "break-word", flex: 1 }}>
            {job?.file_name || job?.job_id || "Unnamed job"}
          </div>
          <Badge color={getStatusColor(jobStatus)} pill className="text-capitalize">
            {job?.status || "Unknown"}
          </Badge>
        </div>

        <div className="mb-2">
          <div className="d-flex justify-content-between small text-muted mb-1">
            <span>Progress</span>
            <span>{jobProgress}%</span>
          </div>
          <Progress value={jobProgress} color={getStatusColor(jobStatus)} style={{ height: 8 }} />
        </div>

        {jobTotalCamps > 0 && (
          <div className="text-muted small mb-2">
            Camps: {jobCompletedCamps} / {jobTotalCamps}
            {job?.current_camp_name ? ` · ${job.current_camp_name}` : ""}
          </div>
        )}

        <div className="small text-muted mb-1" style={{ wordBreak: "break-word" }}>
          <strong>Job ID:</strong> {job?.job_id || "—"}
        </div>
        <div className="small text-muted mb-1">
          <strong>Type:</strong> {formatMasterlistType(jobMeta?.masterlist_type)}
        </div>
        <div className="small text-muted mb-1">
          <strong>Location:</strong> {formatLocation(jobMeta?.location)}
        </div>
        <div className="small text-muted mb-3">
          <strong>Started:</strong> {formatDateTime(job?.started_at || job?.requested_at)}
        </div>

        <Button color="primary" size="sm" outline className="w-100" onClick={() => onTrack(job)} disabled={!job?.queue_job_id}>
          Track Job
        </Button>
      </CardBody>
    </Card>
  );
};

const MasterListJobTracker = () => {
  const { queueJobId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const initialJob = location?.state?.job || null;

  const [jobData, setJobData] = useState(initialJob);
  const [isLoading, setIsLoading] = useState(!initialJob);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [pollError, setPollError] = useState("");
  const [passwordCopied, setPasswordCopied] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!queueJobId) return null;
    if (!silent) setIsLoading(true);
    try {
      const res = await genericGet(
        `${ENDPOINTS.MASTERLIST_STATUS}/${encodeURIComponent(queueJobId)}`
      );
      if (res?.status === 200 && (res.data?.status === SUCCESS_RESULT || res.data?.status === DEFAULT_RESULT)) {
        setJobData(res.data.data);
        setPollError("");
        return res.data.data;
      }
      const message = res?.data?.message || "Failed to load job status";
      setPollError(message);
      if (!silent) cogoToast.error(message, { hideAfter: 4 });
      return null;
    } catch (err) {
      const message = err?.message || "Failed to load job status";
      setPollError(message);
      if (!silent) cogoToast.error(message, { hideAfter: 4 });
      return null;
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [queueJobId]);

  const copyMasterlistPassword = async () => {
    const password = jobData?.masterlist_password;
    if (!password || passwordCopied) return;
    try {
      await navigator.clipboard.writeText(String(password));
      setPasswordCopied(true);
      cogoToast.success("Password copied to clipboard.", { hideAfter: 3 });
    } catch {
      cogoToast.error("Failed to copy password.", { hideAfter: 3 });
    }
  };

  useEffect(() => {
    setDeletedFiles([]);
    setPollError("");
    setPasswordCopied(false);
    const passedJob = location?.state?.job;
    if (passedJob?.queue_job_id === queueJobId) {
      setJobData(passedJob);
    }
    fetchStatus(!passedJob || passedJob?.queue_job_id !== queueJobId);
  }, [queueJobId, fetchStatus, location?.state?.job]);

  useEffect(() => {
    if (isTerminalStatus(jobData?.status)) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }

    pollRef.current = setInterval(async () => {
      const latest = await fetchStatus(true);
      if (isTerminalStatus(latest?.status) && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobData?.status, fetchStatus]);

  const cancelJob = async () => {
    const targetJobId = jobData?.job_id;
    if (!targetJobId) return;

    setIsCancelling(true);
    try {
      const res = await genericPost(ENDPOINTS.MASTERLIST_CANCEL, { job_id: targetJobId });
      if (
        res?.status === 200 &&
        (res.data?.status === SUCCESS_RESULT || res.data?.status === DEFAULT_RESULT)
      ) {
        const cancelledJob = res.data?.data?.job || res.data?.data;
        setJobData(cancelledJob);
        setDeletedFiles(Array.isArray(res.data?.data?.deleted_files) ? res.data.data.deleted_files : []);
        setPollError("");
        setShowCancelModal(false);
        cogoToast.success(res.data?.message || "Export cancelled successfully.", { hideAfter: 4 });
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } else {
        const message = res.data?.message || "Failed to cancel export";
        setPollError(message);
        cogoToast.error(message, { hideAfter: 5 });
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to cancel export";
      setPollError(message);
      cogoToast.error(message, { hideAfter: 5 });
    } finally {
      setIsCancelling(false);
    }
  };

  const progress = Math.min(100, Math.max(0, Number(jobData?.progress) || 0));
  const status = String(jobData?.status || "unknown").toLowerCase();
  const meta = jobData?.meta || {};
  const totalCamps = Number(jobData?.total_camps) || 0;
  const completedCamps = Number(jobData?.completed_camps) || 0;
  const campProgress =
    totalCamps > 0 ? Math.min(100, Math.round((completedCamps / totalCamps) * 100)) : 0;
  const campNames = Array.isArray(meta?.camp_names) ? meta.camp_names : [];
  const hasCampProgress = totalCamps > 0 || campNames.length > 0;
  const masterlistPassword =
    status === "completed" && jobData?.masterlist_password
      ? String(jobData.masterlist_password)
      : "";
  const otherActiveJobs = Array.isArray(jobData?.other_active_jobs)
    ? jobData.other_active_jobs.filter(
        (j) => j?.queue_job_id && j.queue_job_id !== (jobData?.queue_job_id || queueJobId)
      )
    : [];

  const trackOtherJob = (job) => {
    if (!job?.queue_job_id) return;
    history.push({
      pathname: `/projects/masterlist-job-tracker/${encodeURIComponent(job.queue_job_id)}`,
      state: { job },
    });
  };

  return (
    <div className="container-fluid py-4">
      <Breadcrumb>
        <BreadcrumbItem>
          <Link to="/projects/list">Projects</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/view">View Project</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Masterlist Job Tracker</BreadcrumbItem>
      </Breadcrumb>

      <Row className="mt-3 g-3">
        <Col lg={8}>
      <Card className="shadow-sm">
        <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="mb-0">Masterlist Export Job</h4>
          <div className="d-flex align-items-center gap-2">
            <Badge color={getStatusColor(status)} pill style={{ fontSize: "0.85rem", padding: "6px 12px" }}>
              {jobData?.status || "Unknown"}
            </Badge>
            {!isTerminalStatus(status) && <Spinner size="sm" color="primary" />}
          </div>
        </CardHeader>
        <CardBody>
          {isLoading && !jobData ? (
            <div className="text-center text-muted py-5">
              <Spinner className="me-2" />
              Loading job status...
            </div>
          ) : (
            <>
              {pollError && (
                <Alert color="danger" className="mb-3">
                  {pollError}
                </Alert>
              )}

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold">Progress</span>
                  <span>{progress}%</span>
                </div>
                <Progress
                  value={progress}
                  color={
                    status === "failed"
                      ? "danger"
                      : status === "completed"
                      ? "success"
                      : status === "cancelled"
                      ? "warning"
                      : "primary"
                  }
                />
              </div>

              {hasCampProgress && (
                <div
                  className="mb-4 p-3"
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <span className="fw-semibold">Camp Generation Progress</span>
                    {totalCamps > 0 && (
                      <span className="text-muted small">
                        {completedCamps} of {totalCamps} camps completed
                      </span>
                    )}
                  </div>
                  {totalCamps > 0 && (
                    <Progress value={campProgress} color="info" className="mb-3" />
                  )}
                  <div className="row g-3">
                    <div className="col-sm-6 col-md-3">
                      <DetailRow label="Total Camps" value={totalCamps || "—"} />
                    </div>
                    <div className="col-sm-6 col-md-3">
                      <DetailRow label="Completed Camps" value={completedCamps || "—"} />
                    </div>
                    <div className="col-sm-6 col-md-3">
                      <DetailRow label="Current Camp Index" value={jobData?.current_camp_index ?? "—"} />
                    </div>
                    <div className="col-sm-6 col-md-3">
                      <DetailRow label="Current Camp" value={jobData?.current_camp_name || "—"} />
                    </div>
                  </div>
                  {campNames.length > 0 && (
                    <div className="mt-2">
                      <div className="text-muted small mb-2">Camp Names ({campNames.length})</div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          maxHeight: 120,
                          overflowY: "auto",
                        }}
                      >
                        {campNames.map((camp, idx) => {
                          const isCurrent =
                            jobData?.current_camp_name &&
                            String(camp).toLowerCase() ===
                              String(jobData.current_camp_name).toLowerCase();
                          const isDone = totalCamps > 0 && idx < completedCamps;
                          return (
                            <Badge
                              key={`${camp}-${idx}`}
                              color={isCurrent ? "primary" : isDone ? "success" : "light"}
                              style={{
                                fontWeight: 600,
                                color: isCurrent || isDone ? "#fff" : "#475569",
                                border: isCurrent || isDone ? "none" : "1px solid #e2e8f0",
                              }}
                            >
                              {camp}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="row g-4">
                <div className="col-md-6">
                  <DetailRow label="Job ID" value={jobData?.job_id} />
                  <DetailRow label="Queue Job ID" value={jobData?.queue_job_id || queueJobId} />
                  <DetailRow label="File Name" value={jobData?.file_name} />
                  <DetailRow label="Masterlist Type" value={formatMasterlistType(meta?.masterlist_type)} />
                  <DetailRow label="Location" value={formatLocation(meta?.location)} />
                </div>
                <div className="col-md-6">
                  <DetailRow label="Requested At" value={formatDateTime(jobData?.requested_at)} />
                  <DetailRow label="Started At" value={formatDateTime(jobData?.started_at)} />
                  <DetailRow label="Completed At" value={formatDateTime(jobData?.completed_at)} />
                  <DetailRow label="Expires At" value={formatDateTime(jobData?.expires_at)} />
                  {jobData?.error && (
                    <Alert color="danger" className="mt-2 mb-0">
                      <strong>Error:</strong> {jobData.error}
                    </Alert>
                  )}
                </div>
              </div>

              {(jobData?.download_url || masterlistPassword) && (
                <div className="mt-4 d-flex flex-column flex-sm-row flex-wrap align-items-start gap-3">
                  {jobData?.download_url && (
                    <Button
                      color="success"
                      tag="a"
                      href={jobData.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download Masterlist
                    </Button>
                  )}
                  {masterlistPassword && (
                    <div
                      className="p-3"
                      style={{
                        border: "1px solid #bbf7d0",
                        borderRadius: 10,
                        background: "#f0fdf4",
                        minWidth: 260,
                      }}
                    >
                      <div className="text-muted small mb-1">Masterlist Password</div>
                      <div
                        className="mb-2"
                        style={{
                          fontFamily: "monospace",
                          fontSize: "1.1rem",
                          letterSpacing: "0.15em",
                          fontWeight: 700,
                          color: "#166534",
                        }}
                      >
                        {"•".repeat(masterlistPassword.length)}
                      </div>
                      <div className="text-muted small mb-2">You can copy 1 time only.</div>
                      <Button
                        color="success"
                        size="sm"
                        outline
                        onClick={copyMasterlistPassword}
                        disabled={passwordCopied}
                      >
                        {passwordCopied ? "Copied" : "Copy Password"}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {deletedFiles.length > 0 && (
                <Alert color="warning" className="mt-4 mb-0">
                  <strong>Deleted files:</strong>
                  <ul className="mb-0 mt-2">
                    {deletedFiles.map((file) => (
                      <li key={file}>{file}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {!isTerminalStatus(status) && (
                <Alert color="info" className="mt-4 mb-0">
                  This page refreshes automatically every few seconds until the job is completed,
                  failed, or cancelled.
                </Alert>
              )}

              <div className="mt-4 d-flex flex-wrap gap-2">
                {isCancellableStatus(status) && (
                  <Button color="danger" onClick={() => setShowCancelModal(true)} disabled={isCancelling}>
                    {isCancelling ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Cancelling...
                      </>
                    ) : (
                      "Cancel Job"
                    )}
                  </Button>
                )}
                <Button color="secondary" outline onClick={() => fetchStatus(false)} disabled={isCancelling}>
                  Refresh Now
                </Button>
              
              </div>
            </>
          )}
        </CardBody>
      </Card>
        </Col>

        <Col lg={4}>
          <div style={{ position: "sticky", top: 16 }}>
            <Card className="shadow-sm">
              <CardHeader className="py-3">
                <h5 className="mb-0">Other Active Jobs</h5>
                <div className="text-muted small mt-1">
                  {otherActiveJobs.length} job{otherActiveJobs.length === 1 ? "" : "s"} running
                </div>
              </CardHeader>
              <CardBody
                className="p-3"
                style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }}
              >
                {otherActiveJobs.length === 0 ? (
                  <div className="text-muted small text-center py-4">
                    No other active export jobs.
                  </div>
                ) : (
                  otherActiveJobs.map((job) => (
                    <OtherActiveJobCard
                      key={job.job_id || job.queue_job_id}
                      job={job}
                      onTrack={trackOtherJob}
                    />
                  ))
                )}
              </CardBody>
            </Card>
          </div>
        </Col>
      </Row>

      <Modal isOpen={showCancelModal} toggle={() => !isCancelling && setShowCancelModal(false)}>
        <ModalHeader toggle={() => !isCancelling && setShowCancelModal(false)}>
          Cancel Export Job
        </ModalHeader>
        <ModalBody>
          Are you sure you want to cancel this masterlist export?
          <div className="mt-2 text-muted small" style={{ wordBreak: "break-word" }}>
            Job: {jobData?.job_id || "—"}
            {jobData?.queue_job_id ? (
              <div className="mt-1">Queue: {jobData.queue_job_id}</div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
            Keep Running
          </Button>
          <Button color="danger" onClick={cancelJob} disabled={isCancelling}>
            {isCancelling ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cancelling...
              </>
            ) : (
              "Confirm Cancel"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MasterListJobTracker;
